import { useState } from "react";
import { api } from "@/lib/api";
import { useNavigate, Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { Lock, Eye, EyeOff } from "lucide-react";

// Single super-admin email constant. The login UI only asks for a password.
// If we ever need multi-admin support, re-introduce an email field.
const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || "admin@gcprimo.com";

export default function AdminLoginPage() {
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      toast.error("Enter your admin password");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/auth/admin/login", { email: ADMIN_EMAIL, password });
      localStorage.setItem("gf_admin_token", r.data.token);
      toast.success(`Welcome, ${r.data.user?.name || "Admin"}`);
      nav("/admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="Admin sign in" noindex />
      <div
        className="min-h-screen flex items-center justify-center px-5 py-10"
        style={{ background: "#F3F4F6" }}
        data-testid="admin-login-page"
      >
        <form
          onSubmit={submit}
          className="w-full max-w-md bg-white rounded-[28px] px-8 sm:px-10 py-12 text-center"
          style={{ boxShadow: "0 24px 60px -20px rgba(15, 23, 42, 0.18), 0 8px 20px -12px rgba(15, 23, 42, 0.10)" }}
        >
          {/* Gradient lock badge */}
          <div
            className="mx-auto w-20 h-20 rounded-[22px] flex items-center justify-center text-white"
            style={{
              background:
                "linear-gradient(135deg, #C026D3 0%, #DB2777 40%, #F97316 100%)",
              boxShadow:
                "0 14px 32px -10px rgba(219, 39, 119, 0.55), 0 8px 16px -8px rgba(249, 115, 22, 0.45)",
            }}
            data-testid="admin-login-logo"
          >
            <Lock className="w-9 h-9" strokeWidth={2.5} />
          </div>

          <h1 className="mt-7 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            Admin Dashboard
          </h1>
          <p className="mt-3 text-slate-500 text-base">
            Enter your password to continue
          </p>

          {/* Password field */}
          <div className="mt-10 text-left">
            <label htmlFor="admin-pwd" className="block text-base font-medium text-slate-900 mb-2">
              Password
            </label>
            <div
              className={`flex items-center gap-2 rounded-2xl border-2 px-5 py-4 transition-colors ${
                password ? "border-sky-400" : "border-slate-200 focus-within:border-sky-400"
              }`}
              style={{ boxShadow: password ? "0 0 0 4px rgba(56, 189, 248, 0.18)" : "none" }}
            >
              <input
                id="admin-pwd"
                type={show ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                autoComplete="current-password"
                autoFocus
                data-testid="admin-login-password"
                className="flex-1 bg-transparent outline-none text-base text-slate-900 placeholder:text-slate-400"
              />
              <button
                type="button"
                onClick={() => setShow((v) => !v)}
                aria-label={show ? "Hide password" : "Show password"}
                data-testid="admin-login-toggle"
                className="text-slate-400 hover:text-slate-700 p-1"
              >
                {show ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Login pill button */}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="mt-10 w-full inline-flex items-center justify-center gap-3 h-16 rounded-full text-white text-lg font-semibold transition-all disabled:opacity-60"
            style={{
              background: loading ? "#1F2937" : "#0F172A",
              boxShadow: "0 12px 28px -10px rgba(15, 23, 42, 0.45)",
            }}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                Signing in…
              </span>
            ) : (
              <>
                <Lock className="w-5 h-5" /> Login
              </>
            )}
          </button>

          <div className="mt-5">
            <Link
              to="/admin/forgot-password"
              data-testid="admin-forgot-link"
              className="text-sm text-slate-500 hover:text-slate-900 underline-offset-4 hover:underline"
            >
              Forgot password?
            </Link>
          </div>
        </form>
      </div>
    </>
  );
}
